import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { debounce } from '@deltek/specification-client/core/Utilities/formUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import DeleteIcon from '@material-ui/icons/Delete'
import clsx from 'clsx'
import React, { ChangeEvent, useCallback } from 'react'
import { validateGoogleVimeoDomain } from './VideosTabContent'

const videoLinkItemStyles = makeStyles((theme: Theme) => ({
    videoLinkItemRow: {
        marginTop: theme.spacing(1),
    },
    linkGridContainer: {
        paddingTop: theme.spacing(1),
    },
    linkComponent: {
        '& input': {
            lineHeight: '21px',
            fontSize: pxToRem(18),
        },
        '& .MuiFormLabel-root': {
            color: theme.palette.primary.main,
            marginTop: -4,
            '&.Mui-focused,&.MuiFormLabel-filled': {
                marginTop: 0,
            },
        },
        '& .MuiInputBase-root': {
            height: 47,
            color: theme.palette.primary.main,
        },
    },
    trashcan: {
        marginTop: theme.spacing(0.5),
        marginLeft: theme.spacing(1.5),
    },
    divider: {
        marginTop: `${theme.spacing(2)}px!important`,
        backgroundColor: '#c9c9c9!important',
    },
}))

export interface VideoLinkProps {
    link: string
    validLink: boolean
    order: number
}

export interface VideoLinkItemProps {
    defaultVideoLinkProps: VideoLinkProps
    id: string
    onRemove: (videoRecordId: string) => void
    onValueChange: (value: Record<number, VideoLinkProps>) => void
    order: number
    showDivider?: boolean
}

const VideoLinkItem: React.FC<VideoLinkItemProps> = ({
    defaultVideoLinkProps: { link: defaultLink, validLink },
    id,
    onRemove,
    onValueChange,
    order,
    showDivider = true,
}) => {
    const classes = videoLinkItemStyles()

    const debouncedValueChange = useCallback(
        debounce((currentInput: HTMLInputElement) => {
            onValueChange({
                [id]: {
                    link: currentInput.value,
                    order,
                    validLink: validateGoogleVimeoDomain(currentInput.value),
                },
            })
        }),
        [onValueChange]
    )

    const handleDeleteClick = () => {
        onRemove(id)
    }

    const handleTextfieldChange = (event: ChangeEvent<HTMLInputElement>) => {
        const currentInput = event.currentTarget as HTMLInputElement
        debouncedValueChange(currentInput)
    }

    return (
        <>
            <GridContainer
                justifyContent="space-around"
                className={clsx(classes.videoLinkItemRow, 'videoLinkItemRow')}
            >
                <GridItem xs={10} sm={11} md={11}>
                    <GridContainer
                        direction="column"
                        justifyContent="space-around"
                        spacing={1}
                        className={classes.linkGridContainer}
                    >
                        <GridItem>
                            <TextField
                                label="Add YouTube or Vimeo link here"
                                variant="outlined"
                                fullWidth
                                className={clsx(classes.linkComponent)}
                                inputProps={{
                                    className: 'videoLinkInput',
                                }}
                                required
                                error={!validLink}
                                defaultValue={defaultLink}
                                helperText={
                                    !validLink
                                        ? 'Must enter a valid YouTube or Vimeo link'
                                        : ''
                                }
                                onChange={handleTextfieldChange}
                            />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem xs={2} sm={1} md={1}>
                    <IconButton
                        className={clsx(
                            classes.trashcan,
                            'linkItemDeleteButton'
                        )}
                        onClick={handleDeleteClick}
                    >
                        <DeleteIcon color="primary" />
                    </IconButton>
                </GridItem>
            </GridContainer>
            {showDivider && <Divider className={classes.divider} />}
        </>
    )
}

export default VideoLinkItem
